import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, XCircle, AlertCircle, Loader2 } from "lucide-react";
import { useIsAdmin, useIsAdminLegacy, useUserRole } from "@/hooks/useUserRole";
import { useEffect, useState } from "react";

/**
 * Componente de teste para FASE 2
 * Compara o novo sistema (user_roles) com o sistema legado (profiles.is_admin)
 * NÃO MODIFICAR CÓDIGO EXISTENTE - apenas para testes
 */
export function RoleSystemTest() {
  const { isAdmin: newIsAdmin, isLoading: newLoading } = useIsAdmin();
  const { data: legacyIsAdmin, isLoading: legacyLoading } = useIsAdminLegacy();
  const { data: roles, isLoading: rolesLoading } = useUserRole();
  
  const [testResult, setTestResult] = useState<'pass' | 'fail' | 'pending'>('pending');

  useEffect(() => {
    if (!newLoading && !legacyLoading) {
      // Compara os dois sistemas
      if (newIsAdmin === legacyIsAdmin) {
        setTestResult('pass');
      } else {
        setTestResult('fail');
      }
    }
  }, [newIsAdmin, legacyIsAdmin, newLoading, legacyLoading]);

  const isLoading = newLoading || legacyLoading || rolesLoading;

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>🧪 Teste do Sistema de Roles (FASE 2)</CardTitle>
          <CardDescription>Validando migração segura...</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🧪 Teste do Sistema de Roles (FASE 2)
          {testResult === 'pass' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
          {testResult === 'fail' && <XCircle className="w-5 h-5 text-red-500" />}
        </CardTitle>
        <CardDescription>
          Comparação entre sistema novo (user_roles) e legado (profiles.is_admin)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Resultado do Teste */}
        {testResult === 'pass' && (
          <Alert className="border-green-500 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-900">✅ Teste Aprovado!</AlertTitle>
            <AlertDescription className="text-green-800">
              Os dois sistemas retornam o mesmo resultado. A migração está funcionando corretamente.
            </AlertDescription>
          </Alert>
        )}

        {testResult === 'fail' && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>❌ Teste Falhou!</AlertTitle>
            <AlertDescription>
              Os sistemas retornam resultados diferentes. Revisar migração dos dados.
            </AlertDescription>
          </Alert>
        )}

        {/* Comparação Lado a Lado */}
        <div className="grid grid-cols-2 gap-4">
          {/* Sistema Novo */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <h3 className="font-semibold text-blue-900 mb-2">
              🆕 Sistema Novo (user_roles)
            </h3>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-600">É Admin:</span>
                <Badge className="ml-2" variant={newIsAdmin ? "default" : "secondary"}>
                  {newIsAdmin ? "SIM" : "NÃO"}
                </Badge>
              </div>
              <div>
                <span className="text-sm text-gray-600">Roles:</span>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {roles?.map(role => (
                    <Badge key={role} variant="outline">
                      {role}
                    </Badge>
                  )) || <span className="text-xs text-gray-500">Nenhum</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Sistema Legado */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="font-semibold text-gray-900 mb-2">
              🕐 Sistema Legado (profiles.is_admin)
            </h3>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-600">É Admin:</span>
                <Badge className="ml-2" variant={legacyIsAdmin ? "default" : "secondary"}>
                  {legacyIsAdmin ? "SIM" : "NÃO"}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Informações Adicionais */}
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>ℹ️ Sobre este Teste</AlertTitle>
          <AlertDescription className="text-sm space-y-1">
            <p>• Este componente NÃO modifica o código existente</p>
            <p>• Sistemas antigo e novo funcionam em paralelo</p>
            <p>• Rollback é possível a qualquer momento</p>
            <p>• Após validação completa (30 dias), campo is_admin será removido</p>
          </AlertDescription>
        </Alert>

        {/* Estatísticas */}
        <div className="border-t pt-4 text-xs text-gray-500 space-y-1">
          <p>🔍 Query Key (novo): ['user-role']</p>
          <p>🔍 Query Key (legado): ['user-admin-legacy']</p>
          <p>📊 Cache: 5 minutos</p>
        </div>
      </CardContent>
    </Card>
  );
}
